import { useEffect, useMemo } from "react";
import { useQuery, useMutation, UseMutationOptions, UseQueryOptions, UseQueryResult, QueryFunction, MutationFunction } from "react-query";
import capitalize from "lodash/capitalize";

import { Resource, ResponseList, ResponseOne, usePagination } from "@imminently/react/store/query";
import { useNotify } from "@imminently/react/store/notifications";

import { ActionName, CreateResourceServiceConfig, GetListFilters, PubSubMessage, UseResourceOptions } from "./types";
import { PubSubSubject } from "./PubSubSubject";

// QueryObserver looks like it could be used instead of sagas
// can add sub methods so you can drive logic off cache updates

/**
 * WIP this is a new factory concept for creating a resource service
 * Some of the methds are test only and are subject to change
 * The core DataProvider wrappers and crud hooks should remain the same
 */
export const createResourceService = <T extends Resource>({ resource, client, dataProvider, queryClient, key, parent }: CreateResourceServiceConfig) => {
  const queryKey = key ?? resource;

  // #region Basic CRUD
  const getList = async (options: GetListFilters = {}) => {
    if (!dataProvider) throw new Error("No data provider");
    const params = { parent, ...options };
    return await dataProvider.getList(resource, params) as ResponseList<T>;
  };

  const getOne = async (id: string) => {
    if (!dataProvider) throw new Error("No data provider");
    const res = await dataProvider.getOne(resource, { id, parent }) as ResponseOne<T>;
    return res.data;
  };

  const create = async (payload: Partial<T>) => {
    if (!dataProvider) throw new Error("No data provider");
    const res = await dataProvider.create(resource, { payload, parent }) as ResponseOne<T>;
    return res.data;
  };

  type UpdateProps = { id: string, payload: Partial<T> };
  const update = async ({ id, payload }: UpdateProps) => {
    if (!dataProvider) throw new Error("No data provider");
    const res = await dataProvider.update(resource, { id, payload, parent }) as ResponseOne<T>;
    return res.data;
  };

  const remove = async (id: string) => {
    if (!dataProvider) throw new Error("No data provider");
    const res = await dataProvider.delete(resource, { id, parent }) as ResponseOne<T>;
    return res.data;
  };

  // #endregion

  // #region Hooks

  type QueryOptions = Omit<UseQueryOptions, "queryKey" | "queryFn">;
  const useGetOne = (id: string, options?: QueryOptions) => {
    const queryFn: QueryFunction = () => getOne(id);
    return useQuery([queryKey, id], queryFn, options) as UseQueryResult<T>;
  };

  const useGetList = (options: QueryOptions = {}) => {
    const queryFn: QueryFunction = ({ meta }) => getList(meta);
    // Must use all meta as part of the query key to ensure refetching
    const filter = options?.meta ?? {};

    return useQuery(
      [queryKey, JSON.stringify(filter)],
      queryFn,
      options
    ) as UseQueryResult<ResponseList<T>>;

    // WIP trying to force refetch on meta change
    // right now it only works on page change
    // any meta change requires the user to manually refetch
    // ideally this should auto refresh, however this exact method causes an infinite loop
    // console.log("[useGetList]", [queryKey, page]);
    // const { meta } = options;

    // useEffect(() => {
    //   query.refetch();
    // }, [meta]);

    // return query;
  };

  type MutationOptions = Omit<UseMutationOptions, "mutationFn">;

  const updateCache = (data: T, qKey: string = resource) => {
    try {
      queryClient.setQueryData([qKey, { id: data.id }], data);
      queryClient.invalidateQueries(qKey);

      if (parent) {
        // make sure we update the parent resource as it likely wants the update as well
        queryClient.setQueryData([parent.resource, parent.id], (item: any) => {
          const subResource = item[qKey] ?? [];
          const res = subResource.map((subItem: any) => {
            if (subItem.id === data.id) return data;
            return subItem;
          });
          return { ...item, [qKey]: res };
        });
      }
    } catch (err) {
      console.log("[updateCache] Failed to updateCache", err);
    }
  };

  const addCache = (data: T, qKey: string = resource) => {
    try {
      // check if exists, if so, send to update instead
      const existing = queryClient.getQueryData([qKey, { id: data.id }]);
      if (existing) {
        updateCache(data, qKey);
        return;
      }

      // data needs to default to empty array in case it doesn't exist yet
      queryClient.setQueryData(qKey, (old: any = { data: [] }) => {
        const res = [...old.data, data];
        // sort by created
        res.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        return { total: res.length, data: res };
      });
      queryClient.invalidateQueries([qKey, { id: data.id }]);

      if (parent) {
        // make sure we update the parent resource as it likely wants the update as well
        queryClient.setQueryData([parent.resource, parent.id], (item: any) => {
          const subResource = item[qKey] ?? [];
          return { ...item, [qKey]: [...subResource, data] };
        });
      }
    } catch (err) {
      console.log("[addCache] Failed to addCache", err);
    }
  };

  // remove will only ever be by id
  const removeCache = (id: string, qKey: string = resource) => {
    try {
      queryClient.setQueryData(qKey, (old: any) => {
        const res = old.data.filter((item: any) => item.id !== id);
        return { ...old, data: res };
      });
      queryClient.invalidateQueries([qKey, { id: id }]);

      if (parent) {
        // make sure we update the parent resource as it likely wants the update as well
        queryClient.setQueryData([parent.resource, parent.id], (item: any) => {
          const subResource = item[qKey] ?? [];
          const res = subResource.filter((subItem: any) => subItem.id !== id);
          return { ...item, [qKey]: res };
        });
      }
    } catch (err) {
      console.log("[removeCache] Failed to removeCache", err);
    }
  };

  const invalideAllQueries = () => {
    queryClient.invalidateQueries({ predicate: (query) => {
      const qKey = query.queryKey;
      if (typeof qKey === "string") return qKey === queryKey;
      return qKey[0] === queryKey;
    }});
  };

  const useCreateResource = (options: MutationOptions = {}) => {
    // @ts-ignore
    return useMutation(create, {
      ...options,
      onSuccess: (data, variables, context) => {
        // We do not want to auto update the cache as we cannot guarantee that its meant to be on screen
        // addCache(data);
        // instead we want to force a refetch
        invalideAllQueries();
        // @ts-ignore
        options?.onSuccess?.(data, variables, context);
      },
    });
  };

  const useUpdateResource = (options?: MutationOptions) => {
    // @ts-ignore
    return useMutation(update, {
      ...options,
      onSuccess: (data, variables, context) => {
        updateCache(data);
        // @ts-ignore
        options?.onSuccess?.(data, variables, context);
      },
    });
  };

  const useDeleteResource = (options?: MutationOptions) => {
    // @ts-ignore
    return useMutation(remove, {
      ...options,
      onSuccess: (data, variables, context) => {
        // data might not have id, so we use variables
        // const id = data?.id ?? variables;
        // if we remove it, the screen might end up with less than expected, ie 9 of 10 results
        // removeCache(id);
        // best to refetch instead
        invalideAllQueries();

        // @ts-ignore
        options?.onSuccess?.(data, variables, context);
      },
    });
  };

  const useResources = (options: UseResourceOptions = {}) => {
    const { filter = {}, pagination: pageProps = { page: 0, perPage: 10 } } = options;
    const { error } = useNotify();

    // TODO interesting, updating the filter is causing usePagination to reset
    // looks like hooks are not working as expected. but this does fix
    // the issue of having to clear the pagination search filter changes
    // TODO is the above still an issue?
    const pagination = usePagination(pageProps.page, pageProps.perPage);
    const { pagination: pageState, setPage } = pagination;

    const { data, ...query } = useGetList({
      keepPreviousData: true,
      meta: { pagination: pageState, parent, ...filter }
    });

    // if query errors, dispatch error notification
    useEffect(() => {
      if (query.isError && query.error) {
        const err = query.error as Error;
        if (err.message) {
          error(err.message);
        } else {
          console.log("Query error is unexpected type", err);
          error("[useResource] Unknown error type");
        }
      }
    }, [query]);

    // if not on page 0 and no data, go back a page
    useEffect(() => {
      if (data?.data.length === 0 && pageState.page > 0) {
        setPage(pageState.page - 1);
      }
    }, [setPage, data, pageState]);

    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      query,
      pagination,
    };
  };

  const DEFAULT_ACTION_NAMES = { create: "add", update: "edit" };

  /**
   * A hook to create or update a resource
   */
  const useMutateResource = (resourceObj: T | undefined, options: MutationOptions, actionNames: ActionName = DEFAULT_ACTION_NAMES) => {
    const { mutateAsync: createAsync, ...cQuery } = useCreateResource(options);
    const { mutateAsync: updateAsync, ...uQuery } = useUpdateResource(options);

    const isUpdate = !!resourceObj;

    const action = (payload: Partial<T>) => {
      if (isUpdate) {
        // isUpdate ensures resource id exists
        return updateAsync({ id: resourceObj?.id as string, payload });
      }
      return createAsync(payload);
    };

    return useMemo(() => ({
      action,
      query: isUpdate ? uQuery : cQuery,
      actionName: {
        lower: isUpdate ? actionNames.update.toLowerCase() : actionNames.create.toLowerCase(),
        captilized: isUpdate ? capitalize(actionNames.update) : capitalize(actionNames.create),
      },
      isUpdate
    }), [action, isUpdate, uQuery, cQuery]);
  };

  const useSubresource = <S extends Resource,>(subResource: string, parentId: string) => {
    return useMemo(() => createResourceService<S>({
      resource: subResource,
      client,
      dataProvider,
      queryClient,
      parent: {
        resource,
        id: parentId
      }
    }), [parentId]);
  };

  // #endregion

  // #region Loaders

  const getOneLoader = async ({ params: { id } }: { params: { id: string } }) => {
    return await queryClient.fetchQuery(resource, () => getOne(id));
  };

  const getListLoader = async () => {
    // react router may not support url search params in loaders
    // need to look through the docs more
    // const filter = parseUrlFilter({});
    return await queryClient.fetchQuery(resource, () => getList());
  };

  // #endregion

  // #region WebSockets

  const getWsUrl = async () => {
    const res = await client.get(`${resource}/custom/pubsub`);
    console.log("[PubSub] res", res.data);
    const { token } = res.data;
    return token as string;
  };

  const handleResourceMessage = (message: PubSubMessage<T>) => {
    const { action, data } = message;
    switch (action) {
      case "create":
        // addCache(data);
        // can't just add it as we don't know what the filters are
        invalideAllQueries();
        break;
      case "update":
        updateCache(data);
        break;
      case "delete":
        // can't just delete as we don't know what the filters are
        invalideAllQueries();
        // TODO this data will need to be or have the id
        // removeCache(data.id);
        break;
    }
  };

  const pubsub$ = new PubSubSubject(getWsUrl);

  // const usePubSub = (callback: (msg: PubSubMessage<T>) => void) => {
  //   const msg = useObservable(pubsub$);
  //   useEffect(() => {
  //     if (msg) {
  //       callback(msg);
  //     }
  //   }, [msg]);
  // };

  // const useSubscribe = <R extends ResourceOrList = T,>(callback: (msg: PubSubMessage<R>) => void) => {
  //   // allow forceful type override
  //   const sub = pubsub$.
  //   useEffect(() => {
  //     callback(msg);
  //   }, [msg]);
  // };

  // #endregion

  const useGetCustom = <R = any,>(action: string, options?: UseQueryOptions<R, unknown, R>) => {
    const url = `${resource}/custom/${action}`;
    const queryFn: QueryFunction<R> = async ({ meta }) => {
      const res = await client.get(url, meta);
      return res.data;
    };
    return useQuery<R, unknown, R>(action, queryFn, options);
  };

  const usePostCustom = <R = any,>(action: string, options?: UseMutationOptions<R, unknown, R>) => {
    const url = `${resource}/custom/${action}`;
    const mutateFn: MutationFunction<R, R> = async (variables) => {
      const res = await client.post(url, variables);
      return res.data;
    };
    return useMutation<R, unknown, R>(action, mutateFn, options);
  };

  return {
    getList,
    getOne,
    create,
    update,
    remove,
    useGetOne,
    useGetList,
    useCreate: useCreateResource,
    useUpdate: useUpdateResource,
    useDelete: useDeleteResource,
    useResources,
    useMutateResource,
    getOneLoader,
    getListLoader,
    getWsUrl,
    handleResourceMessage,
    useSubresource,
    pubsub$,
    // usePubSub,
    useGetCustom,
    usePostCustom,
    addCache,
    updateCache,
    removeCache,
    invalideAllQueries,
  };
};
